import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../App'; // Import your auth hook

// Supported languages with flags
export const SUPPORTED_LANGUAGES = {
  EN: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
    nativeName: 'English'
  },
  IT: {
    code: 'it',
    name: 'Italian',
    flag: '🇮🇹',
    nativeName: 'Italiano'
  }
};

// Default translations structure
const defaultTranslations = {
  en: {},
  it: {}
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, translations = defaultTranslations }) => {
  const { httpClient, user, isAuthenticated } = useAuth(); // Get auth context
  const [language, setLanguage] = useState(SUPPORTED_LANGUAGES.EN.code);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [backendLanguage, setBackendLanguage] = useState(null);

  // API call to get user language preference (JWT version)
  const getUserLanguagePreference = async () => {
    try {
      const response = await httpClient.request('/api/user/language');
      
      if (response.ok) {
        const data = await response.json();
        return data.language;
      } else {
        console.error('Failed to fetch user language preference:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching user language preference:', error);
      return null;
    }
  };

  // API call to update user language preference (JWT version)
  const updateUserLanguagePreference = async (newLanguage) => {
    try {
      const response = await httpClient.request('/api/user/language', {
        method: 'PUT',
        body: JSON.stringify({ language: newLanguage }),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update language preference:', response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error('Error updating language preference:', error);
      return false;
    }
  };

  // Initialize language - this runs once on app load
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        const storedLanguage = localStorage.getItem('easysubs-language');
        
        // First, try to use stored language from localStorage
        if (storedLanguage && Object.values(SUPPORTED_LANGUAGES).some(lang => lang.code === storedLanguage)) {
          setLanguage(storedLanguage);
        } else {
          // Default to Italian
          const defaultLanguage = SUPPORTED_LANGUAGES.IT.code;
          setLanguage(defaultLanguage);
          localStorage.setItem('easysubs-language', defaultLanguage);
        }
      } catch (error) {
        console.error('Error in initial language initialization:', error);
        setLanguage(SUPPORTED_LANGUAGES.EN.code);
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, []);

  // Watch for user authentication changes
  useEffect(() => {
    if (!isInitialized) return;

    const handleUserChange = async () => {
      if (isAuthenticated && user && httpClient) {
        // User is logged in - fetch their preference from backend
        try {
          setIsLoading(true);
          const backendLanguage = await getUserLanguagePreference();
          
          if (backendLanguage && Object.values(SUPPORTED_LANGUAGES).some(lang => lang.code === backendLanguage)) {
            // Use backend preference
            setLanguage(backendLanguage);
            setBackendLanguage(backendLanguage);
            localStorage.setItem('easysubs-language', backendLanguage);
          } else {
            // User has no backend preference, update backend with current language
            const currentLanguage = localStorage.getItem('easysubs-language') || SUPPORTED_LANGUAGES.EN.code;
            const success = await updateUserLanguagePreference(currentLanguage);
            if (success) {
              setBackendLanguage(currentLanguage);
            }
          }
        } catch (error) {
          console.error('Error handling user language on login:', error);
          // Continue with current language
        } finally {
          setIsLoading(false);
        }
      } else {
        // User logged out - clear backend language and use localStorage
        setBackendLanguage(null);
        setIsLoading(false);
      }
    };

    handleUserChange();
  }, [isAuthenticated, user, httpClient, isInitialized]);

  const changeLanguage = async (newLanguage) => {
    if (!Object.values(SUPPORTED_LANGUAGES).some(lang => lang.code === newLanguage)) {
      console.warn(`Unsupported language: ${newLanguage}`);
      return false;
    }
    
    // Update local state and storage immediately for responsive UI
    setLanguage(newLanguage);
    localStorage.setItem('easysubs-language', newLanguage);

    // Update backend if user is logged in
    if (isAuthenticated && httpClient) {
      try {
        const success = await updateUserLanguagePreference(newLanguage);
        if (success) {
          setBackendLanguage(newLanguage);
        }
        return success;
      } catch (error) {
        console.error('Failed to update backend language preference:', error);
        return false;
      }
    }
    
    return true; // Success for offline/local changes
  };

  const getCurrentLanguage = () => {
    return Object.values(SUPPORTED_LANGUAGES).find(lang => lang.code === language) || SUPPORTED_LANGUAGES.EN;
  };

  const value = {
    language,
    changeLanguage,
    isLoading,
    translations: translations[language] || {},
    supportedLanguages: SUPPORTED_LANGUAGES,
    currentLanguage: getCurrentLanguage(),
    isAuthenticated,
    backendLanguage,
    syncStatus: backendLanguage === language ? 'synced' : 'unsynced'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook for using translations
export const useTranslation = () => {
  const { translations } = useLanguage();
  
  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return fallback || key;
      }
    }
    
    return value;
  };
  
  return { t };
};